/**
 * A mapping of specific prop names to their lowercase DOM-safe equivalents.
 * Used to transform camelCase props into kebab-like or lowercase strings
 * for custom elements or DOM attributes that don't support camelCase.
 */
const renameMap: Record<string, string> = {
  shortDescription: 'shortdescription',
  modalOpen: 'modalopen',
  cardHeight: 'cardheight',
  socialShare: 'socialshare',
  administratorTitle: 'administratortitle',
  mainHeadline: 'mainheadline',
  createdAt: 'createdat',
  updatedAt: 'updatedat',
  publishedAt: 'publishedat',
  documentId: 'documentid',
};

/**
 * List of native boolean HTML attributes which React and browsers expect
 * as boolean, NOT string. These should be passed as booleans directly.
 * See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes
 */
const nativeBooleanProps = new Set([
  'checked',
  'disabled',
  'readonly',
  'required',
  'open',
  'hidden',
  'autofocus',
  'multiple',
  'selected',
  'default',
  'formnovalidate',
  'novalidate',
]);

/**
 * Transforms a given props object into a DOM-safe version by:
 * - Renaming keys based on `renameMap`
 * - Converting boolean values to string equivalents for custom/non-native props
 * - Passing native boolean props as booleans
 * - Skipping `null` or `undefined` values
 *
 * Useful for passing props to web components or HTML elements
 * where attributes must be lowercase and boolean attributes must be strings,
 * except for native boolean attributes which accept booleans directly.
 *
 * @param rest - A record of props to be transformed
 * @returns A new object with DOM-safe keys and properly typed values
 *
 * @example
 * renameDomProps({
 *   shortDescription: 'Hello',
 *   modalOpen: true,
 *   checked: false,
 *   cardHeight: 200,
 *   unusedProp: undefined,
 * });
 * // Returns:
 * // {
 * //   shortdescription: 'Hello',
 * //   modalopen: 'true',
 * //   checked: false,
 * //   cardheight: 200
 * // }
 */
export function renameDomProps(rest: Record<string, any>) {
  const renamedProps: Record<string, any> = {};

  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined || value === null) continue;

    const renamedKey = renameMap[key] ?? key;

    if (typeof value === 'boolean') {
      if (nativeBooleanProps.has(renamedKey)) {
        // Keep boolean as boolean for native boolean props
        renamedProps[renamedKey] = value;
      } else {
        // Convert boolean to string for custom/non-native props
        renamedProps[renamedKey] = value.toString();
      }
    } else {
      renamedProps[renamedKey] = value;
    }
  }

  return renamedProps;
}
